import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import api from '../services/api';

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter();

  // Estado
  const user = ref(null);
  const token = ref(localStorage.getItem('token') || null);
  const refreshToken = ref(localStorage.getItem('refresh_token') || null);
  const permissions = ref([]);
  const isAuthenticated = computed(() => !!token.value);
  const isAdmin = computed(() => user.value?.role === 'admin');

  // Acciones
  const setUser = (userData) => {
    user.value = userData;
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const setToken = (newToken) => {
    token.value = newToken;
    if (newToken) {
      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    } else {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
    }
  };

  const setRefreshToken = (newRefreshToken) => {
    refreshToken.value = newRefreshToken;
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }
  };

  const setPermissions = (newPermissions) => {
    permissions.value = newPermissions;
    localStorage.setItem('permissions', JSON.stringify(newPermissions));
  };

  // Cargar usuario desde localStorage
  const loadUser = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      const storedRefreshToken = localStorage.getItem('refresh_token');
      const storedPermissions = localStorage.getItem('permissions');

      if (storedUser) user.value = JSON.parse(storedUser);
      if (storedToken) {
        token.value = storedToken;
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      }
      if (storedRefreshToken) refreshToken.value = storedRefreshToken;
      if (storedPermissions) permissions.value = JSON.parse(storedPermissions);

      // Verificar el token con el backend
      if (storedToken) {
        const response = await api.get('/auth/users/me');
        setUser(response.data);
      }
    } catch (error) {
      console.error('Error al cargar el usuario:', error);
      logout();
    }
  };

  // Login
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', {
        email: email,
        password: password,
      });

      const { access_token, refresh_token } = response.data;
      setToken(access_token);
      setRefreshToken(refresh_token);

      // Obtener datos del usuario
      const userResponse = await api.get('/auth/users/me');
      setUser(userResponse.data);

      // Obtener permisos (opcional)
      const permissionsResponse = await api.get('/auth/users/me/permissions');
      setPermissions(permissionsResponse.data.permissions);

      return userResponse.data;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  };

  // Logout
  const logout = () => {
    user.value = null;
    token.value = null;
    permissions.value = [];
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    delete api.defaults.headers.common['Authorization'];
    router.push('/login');
  };

  // Verificar si el usuario tiene un permiso específico
  const hasPermission = (permissionName) => {
    return permissions.value.includes(permissionName);
  };

  return {
    user,
    token,
    refreshToken,
    permissions,
    isAuthenticated,
    isAdmin,
    loadUser,
    login,
    logout,
    setUser,
    setToken,
    setPermissions,
    setRefreshToken,
    hasPermission,
  };
});
